// KERESŐ-DEBOUNCE — a beviteli mező azonnali állapota, a lekérdezés késleltetett kulcsa.
//
// MIÉRT KÖZÖS: ugyanez a tizenegy soros blokk (lokális `useState` + `useEffect` + `setTimeout(…, 300)` +
// `clearTimeout`) a lista-oldalakon HUSZONHÁROMSZOR szerepelt bájtra azonosan. A repo DRY-szabálya (CLAUDE.md
// 4. főszabály) a 2. előfordulásnál kötelezővé teszi a közös rétegbe emelést — ez a réteg.
//
// MIÉRT KELL EGYÁLTALÁN: a keresett szöveg a lekérdezés KULCSA (`useListFilters` → URL → query key). Debounce
// nélkül minden leütés új kérés: a kiadás-oldalon ~4, a bevétel-előrejelzésen **30 Odin-olvasás** — egy
// nyolcbetűs szó gépelés közben belefut az Odin percenkénti kérés-plafonjába. Ez a hiba élesben már KÉTSZER
// jog-hibának látszott (a 429 a route-guardon „Nincs jogosultság"-ként jelent meg).
//
// KÉT DOLGOT OLD MEG, AMIT A MÁSOLT VÁLTOZAT NEM:
//
//  1. KÜLSŐ VISSZA-SZINKRON. A másolt blokkban a mező tartalma csak befelé folyt (mező → szűrő). Ha a szűrőt
//     MÁSHONNAN nullázták (a „Szűrők törlése" sor, a vissza-nyíl, egy szűrt link megnyitása), a mezőben ott
//     maradt a régi szöveg, és a következő ütemben VISSZAÍRTA a törölt keresést. Itt a `committed` külső
//     változását a mező átveszi.
//  2. A `commit` IDENTITÁSA NEM DEPENDENCY. A hívók `setFilters`-t adnak át, ami a `useListFilters`-ben
//     minden renderben új függvény — dependencyként tehát minden render ÚJRAINDÍTANÁ a 300 ms-os órát, és
//     egy folyamatosan újrarenderelő lapon a commit sosem futna le. A hívás ref-en át megy, a legfrissebb
//     függvénnyel.
import { useCallback, useEffect, useRef, useState } from 'react';

/** A kereső-késleltetés (ms). Egy helyen, hogy a lapok ne csússzanak el egymástól. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface UseDebouncedSearchResult {
  /** A beviteli mező AZONNALI értéke (kontrollált input). */
  input: string;
  /** A mező `onChange`-e. */
  setInput: (value: string) => void;
}

/**
 * @param committed A KIÍRT (szűrőben élő) keresés — az URL-ből/állapotból.
 * @param commit    Ezt hívjuk a késleltetés után, a trimmelt értékkel.
 */
export function useDebouncedSearch(
  committed: string,
  commit: (next: string) => void,
  delayMs: number = SEARCH_DEBOUNCE_MS,
): UseDebouncedSearchResult {
  const [input, setInput] = useState(committed);

  // A legfrissebb `commit` — lásd a fejléc 2. pontját (NEM dependency).
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  }, [commit]);

  // Az utolsó érték, amit MI írtunk ki. Ez különbözteti meg a saját írásunk visszhangját a KÜLSŐ változástól.
  const ownCommit = useRef(committed);

  // Külső változás (szűrő-törlés, vissza-nyíl, szűrt link) → a mező átveszi.
  useEffect(() => {
    if (committed !== ownCommit.current) {
      ownCommit.current = committed;
      setInput(committed);
    }
  }, [committed]);

  useEffect(() => {
    const trimmed = input.trim();
    // Már ez van kiírva → nincs teendő (és a puszta szóköz-gépelés nem indít kérést).
    if (trimmed === committed) {
      ownCommit.current = committed;
      return;
    }
    const timer = setTimeout(() => {
      ownCommit.current = trimmed;
      commitRef.current(trimmed);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [input, committed, delayMs]);

  const onChange = useCallback((value: string) => setInput(value), []);

  return { input, setInput: onChange };
}
